/**
 * Player Eligibility Predicates
 *
 * Single source of truth for "can this seat be dealt into the next hand?" and
 * "is this seat contesting the live hand?".
 *
 * These two notions were previously re-implemented inline at 5+ call sites with
 * three subtly different filters — most notably the client omitted
 * `awaiting_rebuy` while the server included it, so the Resume button could read
 * as enabled while the server refused. Keep new call sites on these helpers.
 *
 * INTENTIONALLY DEPENDENCY-FREE: no `_generated`, no `@ppwr/poker-engine`, no
 * Convex imports. This module is safe to import from the React Native app
 * (same as `shared/constants.ts`). Do not add runtime imports here.
 */

/** Structural shape shared by `Doc<'players'>` and the app's sanitized player type. */
export type PlayerLike = {
  kind?: string
  state: string
}

/**
 * Seat states that cannot be dealt into the next hand.
 *
 * `awaiting_rebuy` belongs here: the engine maps those players to `out`, so a
 * table of one active human + one awaiting-rebuy human cannot deal a hand.
 */
export const INELIGIBLE_PLAYER_STATES = ['out', 'sittingout', 'awaiting_rebuy'] as const

/**
 * Can this seat be dealt into the next hand?
 *
 * `waiting` counts as eligible — `startNewHandHelper` promotes waiting players
 * at the hand boundary (see the `returningNextHand` promotion in
 * `games/helpers/flow.ts`).
 */
export function isEligibleToPlay(player: PlayerLike): boolean {
  return !(INELIGIBLE_PLAYER_STATES as readonly string[]).includes(player.state)
}

/** How many seats (humans and bots) can be dealt into the next hand? */
export function countEligiblePlayers(players: PlayerLike[]): number {
  return players.filter(isEligibleToPlay).length
}

/** How many human seats can be dealt into the next hand? */
export function countEligibleHumans(players: PlayerLike[]): number {
  return players.filter((p) => p.kind === 'human' && isEligibleToPlay(p)).length
}

/** How many bot seats can be dealt into the next hand? */
export function countEligibleBots(players: PlayerLike[]): number {
  return players.filter((p) => p.kind === 'bot' && isEligibleToPlay(p)).length
}
